/**
 * Ledger Builder
 *
 * @module workflows/diagnostics/materialize/ledger-builder
 * @description Composes `ledger.md` (full chronological narrative) and `ledger-index.md`
 * (step-heading TOC with line numbers) from in-memory event streams. The builder is the
 * single load-bearing artifact for the diagnostic agent: every event from steps, thoughts,
 * actions, navigation, network, console, and assertions appears as one timestamped line.
 *
 * Format invariants (verified by `materialize.test.ts`):
 *   - Every line carries `[T+S.SSSs @ HH:MM:SS.mmm]` prefix when timestamps are known.
 *   - Lines sort strictly by absolute timestamp (with relative time as tie-breaker).
 *   - Step headings sit at the step start; per-step lines follow until the next heading.
 *   - `ledger-index.md` lists each step heading with its `ledger.md` line number.
 *
 * Lines whose absolute timestamp is unknown (e.g. trace events when `runStartedAt` was
 * null on the run record) sort to the end with an `[unknown @ unknown]` prefix.
 */

import * as fs from 'node:fs/promises';
import { resolveWorkspacePath, WORKSPACE_PATHS } from './paths.js';
import { computeStepWorkWindows, formatPrefix as formatTimestampPrefix, oneLine } from './_shared.js';

// ============================================================================
// Inputs
// ============================================================================

export interface LedgerStep {
  id: string;
  nodeId: string;
  orderIndex: number;
  name: string | null;
  nodeType: string | null;
  status: string;
  startedAtMs: number | null;
  finishedAtMs: number | null;
  durationMs: number | null;
  errorMessage: string | null;
  cacheOutcome: string | null;
  cacheHitSource: string | null;
}

export interface LedgerThought {
  stepId: string | null;
  timestampMs: number;
  toolName: string | null;
  content: string;
}

export interface LedgerAction {
  action: string;
  target: string | null;
  url: string | null;
  timestampMs: number | null;
  success: boolean;
  isAnnotation: boolean;
}

export interface LedgerNavigation {
  timestampMs: number | null;
  action: string;
  from: string | null;
  to: string | null;
  crossHost: boolean;
}

export interface LedgerNetwork {
  timestampMs: number | null;
  url: string;
  method: string;
  status: number | null;
}

export interface LedgerConsole {
  timestampMs: number | null;
  type: string;
  text: string;
}

export interface LedgerAssertion {
  stepId: string;
  timestampMs: number | null;
  condition: string | null;
  severity: string | null;
  nonBlocking: boolean;
  passed: boolean;
  reason: string | null;
}

export interface LedgerInput {
  workspaceRoot: string;
  runId: string;
  workflowId: string;
  workflowName: string;
  runStatus: string;
  /** ISO; canonical baseline for `T+...s` relative timestamps. */
  runStartedAt: string | null;
  reportedFailure: {
    failedStepName: string | null;
    failedStepOrderIndex: number | null;
    errorMessage: string | null;
  };
  steps: ReadonlyArray<LedgerStep>;
  thoughts: ReadonlyArray<LedgerThought>;
  actions: ReadonlyArray<LedgerAction>;
  navigation: ReadonlyArray<LedgerNavigation>;
  network: ReadonlyArray<LedgerNetwork>;
  consoleLogs: ReadonlyArray<LedgerConsole>;
  assertions: ReadonlyArray<LedgerAssertion>;
}

// ============================================================================
// Internal event union
// ============================================================================

type LedgerEvent =
  | { kind: 'step'; step: LedgerStep; timestampMs: number | null }
  | { kind: 'thought'; thought: LedgerThought; stepId: string | null }
  | { kind: 'action'; action: LedgerAction }
  | { kind: 'nav'; nav: LedgerNavigation }
  | { kind: 'network'; net: LedgerNetwork }
  | { kind: 'console'; con: LedgerConsole }
  | { kind: 'assertion'; assertion: LedgerAssertion };

// ============================================================================
// Builder
// ============================================================================

export const writeLedger = async (input: LedgerInput): Promise<void> => {
  const baselineMs = input.runStartedAt ? Date.parse(input.runStartedAt) : null;

  // True execution windows from step-started/step-completed annotations. A
  // cache/playbook step's record `startedAt` is only its commit moment, so
  // anchoring the heading there would sort it *after* the live actions it owns
  // (they'd mis-group under the prior step). Anchor at the work-window start when
  // available so the heading precedes its own actions and `ledger-index.md`
  // ranges are correct; fall back to the record start otherwise.
  const workWindows = computeStepWorkWindows(input.steps, input.actions);

  // Build the event list. Steps anchor at their work-window start (or record
  // startedAt); everything else sorts in by timestamp.
  const events: LedgerEvent[] = [];
  for (const step of input.steps) {
    events.push({
      kind: 'step',
      step,
      timestampMs: workWindows.get(step.id)?.startMs ?? step.startedAtMs,
    });
  }
  for (const thought of input.thoughts) {
    events.push({ kind: 'thought', thought, stepId: thought.stepId });
  }
  for (const action of input.actions) {
    events.push({ kind: 'action', action });
  }
  for (const nav of input.navigation) {
    events.push({ kind: 'nav', nav });
  }
  for (const net of input.network) {
    events.push({ kind: 'network', net });
  }
  for (const con of input.consoleLogs) {
    events.push({ kind: 'console', con });
  }
  for (const assertion of input.assertions) {
    events.push({ kind: 'assertion', assertion });
  }

  events.sort((a, b) => {
    const ta = eventTimestamp(a);
    const tb = eventTimestamp(b);
    if (ta === null && tb === null) return 0;
    if (ta === null) return 1; // unknowns to the end
    if (tb === null) return -1;
    if (ta !== tb) return ta - tb;
    // Tie-break: step heading comes before the events inside it.
    const ra = eventRank(a);
    const rb = eventRank(b);
    return ra - rb;
  });

  // Render the markdown body. Track line numbers for the index.
  const lines: string[] = [];
  const indexEntries: Array<{ orderIndex: number; name: string | null; line: number }> = [];

  // Header.
  lines.push(`# Run Ledger — runId: ${input.runId} — workflow: ${input.workflowName} — status: ${input.runStatus}`);
  if (baselineMs !== null) {
    lines.push(`runStartedAt: ${input.runStartedAt!}   (all T+ values are relative to this)`);
  } else {
    lines.push(`runStartedAt: (unknown — relative timestamps unavailable)`);
  }
  lines.push('');

  // Reported failure pseudo-section.
  if (input.runStatus === 'failed' && input.reportedFailure.failedStepName) {
    const ordinalPart =
      input.reportedFailure.failedStepOrderIndex !== null
        ? ` (step ${input.reportedFailure.failedStepOrderIndex})`
        : '';
    lines.push(`## ⚠ Reported failure`);
    lines.push(`Step${ordinalPart}: **${input.reportedFailure.failedStepName}**`);
    if (input.reportedFailure.errorMessage) {
      lines.push('');
      lines.push('```');
      lines.push(input.reportedFailure.errorMessage);
      lines.push('```');
    }
    lines.push('');
    lines.push('**Verify before trusting — root cause may be upstream.**');
    lines.push('');
    lines.push('---');
    lines.push('');
  }

  for (const event of events) {
    const ts = eventTimestamp(event);
    const prefix = formatTimestampPrefix(ts, baselineMs);

    if (event.kind === 'step') {
      const s = event.step;
      const meta = [
        `status: ${s.status}`,
        s.durationMs !== null ? `dur ${s.durationMs}ms` : null,
        s.cacheOutcome ? `cache: ${s.cacheOutcome}${s.cacheHitSource ? `/${s.cacheHitSource}` : ''}` : null,
      ]
        .filter((x): x is string => x !== null)
        .join(', ');
      const heading = `## ${prefix} Step ${s.orderIndex}: ${s.name ?? s.nodeId}  (${meta})`;
      // Insert a blank line before each step heading except the first.
      if (lines.length > 0 && lines[lines.length - 1] !== '') lines.push('');
      indexEntries.push({
        orderIndex: s.orderIndex,
        name: s.name,
        line: lines.length + 1, // 1-indexed
      });
      lines.push(heading);
      if (s.errorMessage) {
        lines.push(`${prefix} step.error: ${oneLine(s.errorMessage)}`);
      }
      continue;
    }

    if (event.kind === 'thought') {
      const t = event.thought;
      const tool = t.toolName ? ` [${t.toolName}]` : '';
      lines.push(`${prefix} thought${tool}: ${oneLine(t.content)}`);
      continue;
    }

    if (event.kind === 'action') {
      const a = event.action;
      const target = a.target ? ` ${oneLine(a.target)}` : '';
      const url = a.url ? ` → ${a.url}` : '';
      const status = a.success ? '' : ' (failed)';
      const annotation = a.isAnnotation ? ' [annotation]' : '';
      lines.push(`${prefix} action.${a.action}${annotation}:${target}${url}${status}`);
      continue;
    }

    if (event.kind === 'nav') {
      const n = event.nav;
      const cross = n.crossHost ? ' ⚠ cross-host' : '';
      lines.push(`${prefix} nav.${n.action}: ${n.from ?? '?'} → ${n.to ?? '?'}${cross}`);
      continue;
    }

    if (event.kind === 'network') {
      const n = event.net;
      const status = n.status !== null ? ` ${n.status}` : '';
      lines.push(`${prefix} network: ${n.method} ${n.url}${status}`);
      continue;
    }

    if (event.kind === 'console') {
      const c = event.con;
      lines.push(`${prefix} console.${c.type}: ${oneLine(c.text)}`);
      continue;
    }

    if (event.kind === 'assertion') {
      const a = event.assertion;
      const tag = a.passed ? 'assertion.passed' : 'assertion.failed';
      const severity = a.severity ? ` [${a.severity}${a.nonBlocking ? ' nonBlocking' : ''}]` : '';
      const cond = a.condition ? oneLine(a.condition) : '(no condition)';
      const reason = a.reason ? ` — ${oneLine(a.reason)}` : '';
      lines.push(`${prefix} ${tag}${severity}: ${cond}${reason}`);
      continue;
    }
  }

  if (lines[lines.length - 1] !== '') lines.push('');

  await fs.writeFile(
    resolveWorkspacePath(input.workspaceRoot, WORKSPACE_PATHS.ledger),
    `${lines.join('\n')}`,
    'utf-8',
  );

  // Build the index file.
  const indexLines: string[] = [];
  indexLines.push(`# Ledger Index — runId: ${input.runId}`);
  indexLines.push('');
  indexLines.push('Use `bash("sed -n \'<a>,<b>p\' ledger.md")` to read a step\'s window.');
  indexLines.push('');
  if (indexEntries.length === 0) {
    indexLines.push('(no steps recorded for this run)');
  } else {
    for (let i = 0; i < indexEntries.length; i++) {
      const entry = indexEntries[i]!;
      const next = indexEntries[i + 1];
      const startLine = entry.line;
      const endLine = next ? next.line - 1 : lines.length;
      const name = entry.name ?? '(unnamed step)';
      indexLines.push(`Step ${entry.orderIndex}: ${name} — lines ${startLine}-${endLine}`);
    }
  }
  indexLines.push('');
  await fs.writeFile(
    resolveWorkspacePath(input.workspaceRoot, WORKSPACE_PATHS.ledgerIndex),
    `${indexLines.join('\n')}`,
    'utf-8',
  );
};

const eventTimestamp = (e: LedgerEvent): number | null => {
  switch (e.kind) {
    case 'step':
      return e.timestampMs;
    case 'thought':
      return e.thought.timestampMs;
    case 'action':
      return e.action.timestampMs;
    case 'nav':
      return e.nav.timestampMs;
    case 'network':
      return e.net.timestampMs;
    case 'console':
      return e.con.timestampMs;
    case 'assertion':
      return e.assertion.timestampMs;
  }
};

// Lower rank sorts earlier when timestamps tie. Step headings come before per-step events.
const eventRank = (e: LedgerEvent): number => {
  switch (e.kind) {
    case 'step':
      return 0;
    case 'nav':
      return 1;
    case 'action':
      return 2;
    case 'thought':
      return 3;
    case 'network':
      return 4;
    case 'console':
      return 5;
    case 'assertion':
      return 6;
  }
};
